import React, { useState } from "react";
import { analyzeUsp } from "../../api/businessSheet";
import useLocalStorage from "../../hooks/useLocalStorage";
import { normalizeSheetItems } from "../../utils/businessSheetUtils";
import "./styles/UspAnalysis.scss";

const CATEGORIES = [
  { key: "usp", label: "USP", description: "独自の強み・差別化ポイント", required: 1 },
  { key: "powerWords", label: "パワーワード", description: "ビジネスを印象付けるキャッチーな言葉", required: 6 },
  { key: "itemsProducts", label: "商品・商材", description: "主な提供サービス・商品", required: 3 },
];

const CheckboxGroup = ({ category, candidates, selected, onChange }) => {
  const count = selected.size;

  const handleToggle = (index) => {
    const next = new Set(selected);
    if (next.has(index)) {
      next.delete(index);
    } else if (next.size < category.required) {
      next.add(index);
    }
    onChange(next);
  };

  return (
    <div className="usp-category">
      <div className="usp-category-header">
        <span className="usp-category-title">{category.label}</span>
        <span className="usp-category-desc">{category.description}</span>
        <span className={`usp-count ${count === category.required ? "complete" : ""}`}>
          {count}/{category.required}
        </span>
      </div>
      <div className="usp-items">
        {candidates.map((item, index) => {
          const checked = selected.has(index);
          const disabled = !checked && count >= category.required;
          return (
            <label
              key={index}
              className={`usp-item${checked ? " checked" : ""}${disabled ? " disabled" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => handleToggle(index)}
              />
              {item}
            </label>
          );
        })}
      </div>
    </div>
  );
};

const UspAnalysis = ({ onConfirm, initialValues }) => {
  const { getValue, setValue } = useLocalStorage();
  const [industryName, setIndustryName] = useState(
    () => getValue("usp_industryName") || getValue("gs_industryName") || ""
  );
  const [mainService, setMainService] = useState(
    () => getValue("usp_mainService") || getValue("gs_mainService") || ""
  );
  const [candidates, setCandidates] = useState(() => getValue("usp_candidates") || null);
  const [selected, setSelected] = useState(() => {
    const saved = getValue("usp_selected");
    if (saved) {
      return {
        usp: new Set(saved.usp),
        powerWords: new Set(saved.powerWords),
        itemsProducts: new Set(saved.itemsProducts),
      };
    }
    return { usp: new Set(), powerWords: new Set(), itemsProducts: new Set() };
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const allSelected = CATEGORIES.every((cat) => selected[cat.key].size === cat.required);

  const handleIndustryNameChange = (e) => {
    setIndustryName(e.target.value);
    setValue("usp_industryName", e.target.value);
  };

  const handleMainServiceChange = (e) => {
    setMainService(e.target.value);
    setValue("usp_mainService", e.target.value);
  };

  const handleSelectedChange = (key, next) => {
    const next2 = { ...selected, [key]: next };
    setSelected(next2);
    setValue("usp_selected", {
      usp: [...next2.usp],
      powerWords: [...next2.powerWords],
      itemsProducts: [...next2.itemsProducts],
    });
  };

  const handleAnalyze = async () => {
    if (!industryName.trim() || !mainService.trim()) {
      setError("業種名とサービス内容を入力してください。");
      return;
    }
    setError("");
    setIsLoading(true);
    setCandidates(null);
    setValue("usp_candidates", null);
    const reset = { usp: new Set(), powerWords: new Set(), itemsProducts: new Set() };
    setSelected(reset);
    setValue("usp_selected", { usp: [], powerWords: [], itemsProducts: [] });

    const { data, status } = await analyzeUsp(industryName.trim(), mainService.trim());
    setIsLoading(false);

    if (status !== 200 || !data) {
      setError("AI分析に失敗しました。しばらくしてから再試行してください。");
      return;
    }
    setCandidates(data);
    setValue("usp_candidates", data);
  };

  const handleConfirm = () => {
    if (!allSelected || !candidates) return;
    const pick = (key) =>
      [...selected[key]].sort((a, b) => a - b).map((i) => candidates[key][i]);

    // Issue #91: パワーワード・商品/商材は構造化アイテム。
    // AI が返すのはタイトルのみで、内容・URL はユーザーの手入力に委ねる。
    const toItems = (key, kind) => {
      const titles = pick(key);
      return normalizeSheetItems(initialValues?.[key], kind).map((item, i) => ({
        ...item,
        title: titles[i] ?? "",
      }));
    };

    const result = {
      usp: pick("usp"),
      powerWords: toItems("powerWords", "powerWord"),
      itemsProducts: toItems("itemsProducts", "itemProduct"),
    };

    const message = [
      "【USP】",
      ...result.usp.map((v, i) => `${i + 1}. ${v}`),
      "",
      "【パワーワード】",
      ...result.powerWords.map((v, i) => `${i + 1}. ${v.title}`),
      "",
      "【商品・商材】",
      ...result.itemsProducts.map((v, i) => `${i + 1}. ${v.title}`),
    ].join("\n");
    alert(`シートに反映しました。\n\n${message}`);

    onConfirm(result);
  };

  return (
    <div className="usp-analysis">
      <div className="usp-inputs">
        <div className="usp-input-group">
          <label>あなたの業種名</label>
          <input
            type="text"
            value={industryName}
            onChange={handleIndustryNameChange}
            placeholder="例: 保険業、IT業、飲食業"
          />
        </div>
        <div className="usp-input-group">
          <label>主となるサービス内容</label>
          <input
            type="text"
            value={mainService}
            onChange={handleMainServiceChange}
            placeholder="例: 生命保険の販売・コンサルティング"
          />
        </div>
        {error && <p className="usp-error">{error}</p>}
        <button
          className="usp-analyze-btn"
          onClick={handleAnalyze}
          disabled={isLoading}
        >
          {isLoading ? "分析中..." : "AIで分析する"}
        </button>
      </div>

      {candidates && (
        <div className="usp-results">
          <p className="usp-instruction">
            USPを1つ、パワーワードを6つ、商品・商材を3つ選択してください
          </p>
          {CATEGORIES.map((cat) => (
            <CheckboxGroup
              key={cat.key}
              category={cat}
              candidates={candidates[cat.key] ?? []}
              selected={selected[cat.key]}
              onChange={(next) => handleSelectedChange(cat.key, next)}
            />
          ))}
          <button
            className="usp-confirm-btn"
            onClick={handleConfirm}
            disabled={!allSelected}
          >
            シートに反映する
          </button>
        </div>
      )}
    </div>
  );
};

export default UspAnalysis;
